import * as Yup from 'yup'
import { Address } from '@toasttab/buffet-patterns-address-lookup'
import { Country, CountryCode, getCountryCode } from '../countries'
import { CA, IE, GB, US, NZ, IN, AU } from './countryFields'
import { GetStringFunction, DefaultStrings } from '../strings'

type FieldType = 'text' | 'select'

export type Field = {
  label: string
  name: keyof Address
  type: FieldType
  options?: { label: string; value: string }[]
  containerClassName?: string
  className?: string
  validation?: Yup.StringSchema
  errorMessage?: string
}

/**
 * fieldsForCountry
 * @param {CountryCode} country - A country code
 * @param {GetStringFunction<DefaultStrings>} getString - A string function for obtaining UI strings
 * @param {boolean} required - A boolean indicating if the field is required
 * @param {Country[]} countryOptions - An optional list of country options, that when provided, will expose the country selection drop down list
 * @returns A set of fields for the given country
 */
export const fieldsForCountry = (
  country: CountryCode,
  getString: GetStringFunction<DefaultStrings>,
  required: boolean = true,
  countryOptions?: Country[]
) => {
  switch (country) {
    case CountryCode.CA:
      return CA(getString, required, countryOptions)
    case CountryCode.IE:
      return IE(getString, required, countryOptions)
    case CountryCode.GB:
      return GB(getString, required, countryOptions)
    case CountryCode.IN:
      return IN(getString, required, countryOptions)
    case CountryCode.NZ:
      return NZ(getString, required, countryOptions)
    case CountryCode.AU:
      return AU(getString, required, countryOptions)
    default:
      return US(getString, required, countryOptions)
  }
}

function validationsForCountry(country: CountryCode = CountryCode.US) {
  // Doesn't need to translate the errors
  const t = (key: string) => key
  return fieldsForCountry(country, t).reduce(
    (obj: Record<string, any>, field: Field) => {
      obj[field.name] = field.validation
      return obj
    },
    {}
  )
}

/**
 * validationSchema - An exported schema for use within forms to validate the Address object
 */
export const validationSchema = Yup.object().shape({
  address1: Yup.string().required('Street address is required'),
  address2: Yup.string().when(
    'country',
    ([country]) => validationsForCountry(getCountryCode(country)).address2
  ),
  city: Yup.string().when(
    'country',
    ([country]) => validationsForCountry(getCountryCode(country)).city
  ),
  state: Yup.string().when(
    'country',
    ([country]) => validationsForCountry(getCountryCode(country)).state
  ),
  zipCode: Yup.string().when(
    'country',
    ([country]) => validationsForCountry(getCountryCode(country)).zipCode
  )
})
